import type { JSX } from 'preact';
import { memo } from 'preact/compat';
import type { Rgba } from '../../sim/color.ts';
import {
  type EditorTool,
  type EnvironmentPatch,
  type MapLook,
  type MapMeta,
  type Selection,
  type SunSettings,
  type PropId,
  type TerraceKind,
  type WaterMaterial,
} from '../../sim/map/index.ts';
import type { RecentMap } from '../../sim/settings/index.ts';
import type { TestChoice } from '../../game/testPlay.ts';
import { setPanel } from '../panels.ts';
import type { UiToastTone } from '../toast.ts';
import { ContextStrip, type EditorContextActions } from './contextStrip.tsx';
import type { EditorLogBadge, EditorLogFilter } from './log.ts';
import { EditorMenuBar, resetEditorMenuOpen } from './menuBar.tsx';
import { EditorToolRail } from './toolRail.tsx';
import './toolbar.css';

const MESSAGE_TIMEOUT_MS = 4000;

export type EditorToolbarState = {
  tool: EditorTool;
  draftMaterial: WaterMaterial;
  draftDepth: number;
  draftProp: PropId;
  draftTerraceKind: TerraceKind;
  draftTerraceLevels: number;
  draftTerracePeakM: number;
  draftTerraceRadius: number;
  draftTerraceColor: Rgba;
  draftTerraceColorVariation: number;
  draftTerraceJitter: number;
  draftTerraceElevRand: number;
  draftTerraceRadiusRand: number;
  selection: Selection;
  snapToGrid: boolean;
  showScale: boolean;
  showBoat: boolean;
  showMinimap: boolean;
  /** View → Perf render readout (docs/PERFORMANCE.md). */
  showPerf: boolean;
  showRoutes: boolean;
  hiddenRouteIds: readonly string[];
  routeDrafting: boolean;
  previewLighting: boolean;
  showElevation: boolean;
  showCapFlock: boolean;
  capFlockAmount: number;
  waterlineHintAlpha: number;
  showGrid: boolean;
  hasReferenceImage: boolean;
  showReferenceImage: boolean;
  referenceImageAlpha: number;
  sun: SunSettings;
  look: MapLook;
  canUndo: boolean;
  canRedo: boolean;
  fileName: string;
  mapName: string;
  mapRevision: number;
  playable: boolean;
  testBoats: readonly TestChoice[];
  recentMaps: RecentMap[];
  canDeleteMap: boolean;
  logVisible: boolean;
  logFilter: EditorLogFilter;
  logBadge: EditorLogBadge | null;
  message: string | null;
  messageTone: UiToastTone;
};

export type EditorToolbarActions = EditorContextActions & {
  setTool: (tool: EditorTool) => void;
  setSnapToGrid: (snap: boolean) => void;
  setShowScale: (on: boolean) => void;
  setShowBoat: (on: boolean) => void;
  setShowMinimap: (on: boolean) => void;
  setShowPerf: (on: boolean) => void;
  setShowRoutes: (on: boolean) => void;
  onToggleRouteVisibility: (routeId: string) => void;
  onDeleteRoute: (routeId: string) => void;
  setPreviewLighting: (on: boolean) => void;
  setShowElevation: (on: boolean) => void;
  setShowCapFlock: (on: boolean) => void;
  setCapFlockAmount: (amount: number) => void;
  setWaterlineHintAlpha: (alpha: number) => void;
  setShowGrid: (on: boolean) => void;
  setShowReferenceImage: (on: boolean) => void;
  setReferenceImageAlpha: (alpha: number) => void;
  onReferenceImageAlphaCommit: () => void;
  onLoadReferenceImage: () => void;
  onClearReferenceImage: () => void;
  onSunLive: (sun: Partial<SunSettings>) => void;
  onSunCommit: () => void;
  onLookLive: (patch: Partial<MapLook>) => void;
  onLookCommit: () => void;
  onLookCancel: () => void;
  onAimChromeLive: (patch: Partial<MapLook['aimChrome']>) => void;
  onUndo: () => void;
  onRedo: () => void;
  onNew: () => void;
  onSave: () => void;
  onSaveAs: () => void;
  onSaveAndClose: () => void;
  onExport: () => void;
  onOpen: () => void;
  onOpenRecent: (id: string) => void;
  onImport: () => void;
  onDeleteMap: () => void;
  onTestBoat: (id: string) => void;
  refreshTestBoats: () => void;
  onRename: (raw: string) => void;
  onSetMapMeta: (meta: MapMeta) => void;
  onSetMapSize: (size: Partial<{ width: number; height: number }>) => void;
  onSetLaps: (laps: number) => void;
  onSetEnvironment: (patch: EnvironmentPatch) => void;
  setLogVisible: (on: boolean) => void;
  setLogFilter: (filter: EditorLogFilter) => void;
};

export type EditorToolbarProps = {
  state: EditorToolbarState;
  actions: EditorToolbarActions;
};

export type EditorToolbarOptions = {
  getState: () => Omit<EditorToolbarState, 'message' | 'messageTone'>;
  actions: EditorToolbarActions;
};

export type EditorToolbar = {
  sync: () => void;
  showMessage: (text: string, tone: UiToastTone) => void;
  destroy: () => void;
};

export const EditorToolbarPanel = memo(function EditorToolbarPanel({
  state,
  actions,
}: EditorToolbarProps): JSX.Element {
  return (
    <div class="editor-toolbar" role="toolbar" aria-label="Map editor">
      <EditorMenuBar state={state} actions={actions} />
      <ContextStrip
        tool={state.tool}
        draftMaterial={state.draftMaterial}
        draftDepth={state.draftDepth}
        draftProp={state.draftProp}
        draftTerraceKind={state.draftTerraceKind}
        draftTerraceLevels={state.draftTerraceLevels}
        draftTerracePeakM={state.draftTerracePeakM}
        draftTerraceRadius={state.draftTerraceRadius}
        draftTerraceColor={state.draftTerraceColor}
        draftTerraceColorVariation={state.draftTerraceColorVariation}
        draftTerraceJitter={state.draftTerraceJitter}
        draftTerraceElevRand={state.draftTerraceElevRand}
        draftTerraceRadiusRand={state.draftTerraceRadiusRand}
        selection={state.selection}
        canPlaceProps={!state.showElevation}
        actions={actions}
      />
      <EditorToolRail
        tool={state.tool}
        draftProp={state.draftProp}
        playable={state.playable}
        testBoats={state.testBoats}
        canPlaceProps={!state.showElevation}
        canTransform={!state.showElevation}
        onSetTool={actions.setTool}
        onSetDraftProp={actions.setDraftProp}
        onTestBoat={actions.onTestBoat}
        onRefreshTestBoats={actions.refreshTestBoats}
      />
      <p
        class="ui-toast editor-toolbar-message ui-chrome"
        data-tone={state.messageTone}
        role="status"
        hidden={state.message === null}
      >
        {state.message}
      </p>
    </div>
  );
});

export function mountEditorToolbar(options: EditorToolbarOptions): EditorToolbar {
  let message: string | null = null;
  let messageTone: UiToastTone = 'warning';
  let messageTimer: ReturnType<typeof setTimeout> | null = null;

  const push = (): void => {
    setPanel('editorToolbar', {
      state: { ...options.getState(), message, messageTone },
      actions: options.actions,
    });
  };

  push();

  return {
    sync: push,
    showMessage: (text, tone) => {
      message = text;
      messageTone = tone;
      push();

      if (messageTimer !== null) {
        clearTimeout(messageTimer);
      }

      messageTimer = setTimeout(() => {
        message = null;
        messageTimer = null;
        push();
      }, MESSAGE_TIMEOUT_MS);
    },
    destroy: () => {
      if (messageTimer !== null) {
        clearTimeout(messageTimer);
      }

      resetEditorMenuOpen();
      setPanel('editorToolbar', null);
    },
  };
}
